import type { FileConfig } from '../types/FileConfig';

/**
 * Utilities for detecting and parsing CSV headers in Erdos Miller files
 */

/**
 * Find column names with variations and case-insensitive matching
 * @param headers Array of header column names
 * @param possibleNames Array of possible column names to search for
 * @returns First matching header name or null if not found
 */
export function findColumnName(headers: string[], possibleNames: string[]): string | null {
  for (const name of possibleNames) {
    const found = headers.find(header => header.toLowerCase().includes(name.toLowerCase()));
    if (found) return found;
  }
  return null;
}

/**
 * Find header line and determine which timestamp column is present
 * @param lines Array of CSV file lines
 * @param config File configuration containing expected time columns
 * @returns Object with header line index and time column name (null if generating timestamps)
 */
export function findHeaderLineAndTimeColumn(lines: string[], config: FileConfig): { 
  headerLineIndex: number; 
  timeColumnName: string | null;
} {
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    // Parse the line as CSV headers to check for exact column matches
    const headers = line.split(',').map(h => h.trim());
    
    // Check each possible time column name for exact matches
    for (const timeColumn of config.timeColumns) {
      if (headers.includes(timeColumn)) {
        return { 
          headerLineIndex: i,
          timeColumnName: timeColumn
        };
      }
    }
  }
  
  // Handle files that generate timestamps from sampling rate (no explicit time columns)
  if (config.timeColumns.length === 0 || config.generateTimestamps) {
    // Find the data header line (should contain the expected columns)
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      
      const headers = line.split(',').map(h => h.trim());
      
      // Check if this line contains our expected data columns
      const hasExpectedColumns = config.expectedColumns.some(col => headers.includes(col));
      if (hasExpectedColumns) {
        return {
          headerLineIndex: i,
          timeColumnName: null // No time column - will generate timestamps
        };
      }
    }
    throw new Error(`No data header found with expected columns: ${config.expectedColumns.join(', ')}`);
  }
  
  // If no timestamp column found, throw descriptive error
  throw new Error(`No timestamp column found. Looked for: ${config.timeColumns.join(', ')} in file headers`);
}

/**
 * Legacy function for backward compatibility
 * @param lines Array of CSV file lines
 * @param config File configuration
 * @returns Index of the header line
 */
export function findHeaderLine(lines: string[], config: FileConfig): number {
  const result = findHeaderLineAndTimeColumn(lines, config);
  return result.headerLineIndex;
}

/**
 * Determine if file type has units row after header (per file type basis)
 * @param fileType The file type identifier
 * @returns True if the file type includes a units row after the header
 */
export function hasUnitsRow(fileType: string): boolean {
  // MicroPulse file types that have units rows
  const typesWithUnits = ['EM_MICROPULSE_FLOW', 'EM_MICROPULSE_SURVEY', 'EM_MICROPULSE_TEMP'];
  return typesWithUnits.includes(fileType);
}

/**
 * Helper function to find data start index
 * @param lines Array of CSV file lines
 * @param config File configuration
 * @param fileType The file type identifier
 * @returns Index where the actual data rows begin
 */
export function findDataStart(lines: string[], config: FileConfig, fileType: string): number {
  const headerLineIndex = findHeaderLine(lines, config);
  
  if (hasUnitsRow(fileType)) {
    return headerLineIndex + 2; // Skip header + units line
  } else {
    return headerLineIndex + 1; // Data starts immediately after header
  }
}